import { FieldsRenderer } from '@neo/blocks/forms/fields-renderer'
import type { GenericFieldConfig, GenericFormType } from '@neo/blocks/forms/types'
import { chain } from 'lodash'
import type React from 'react'
import { useMemo } from 'react'
import { CodeEditor } from 'v2/shared/components/code-editor'
import { z } from 'zod'
import { JsonKeyValueEditor } from '../../components/json-key-value-editor'
import { useAppVariablesQuery, useSecretVariablesQuery } from '../../queries'
import { parseJsonData } from '../../utils/http-request-utils'
import type { FormValues } from '.'

interface BodyTabProps {
  form: GenericFormType<FormValues>
  nodeVars: string[]
}

type BodyType = 'none' | 'form_data' | 'json' | 'form_urlencoded'

export const BodyTab: React.FC<BodyTabProps> = ({ form, nodeVars }) => {
  const { data: appVarsResponse } = useAppVariablesQuery()
  const { data: secretVarsResponse } = useSecretVariablesQuery()

  const allSuggestions = useMemo(() => {
    const suggestions = [...nodeVars]

    // Add app variables
    if (appVarsResponse) {
      const appVarNames = chain(appVarsResponse)
        .values()
        .flatten()
        .map((variable) => `{{Secrets.${variable.name}}}`)
        .value()
      suggestions.push(...appVarNames)
    }

    // Add custom secret variables
    if (Array.isArray(secretVarsResponse) && secretVarsResponse.length > 0) {
      const secretVarNames = secretVarsResponse.map((variable) => `{{Secrets.${variable.name}}}`)
      suggestions.push(...secretVarNames)
    }

    return suggestions
  }, [nodeVars, appVarsResponse, secretVarsResponse])

  const fields: GenericFieldConfig<FormValues> = useMemo(() => {
    const getBodyDataForType = (bodyType: BodyType) => {
      const parsedBody = parseJsonData(form.state.values.http_request.body)
      return parsedBody[bodyType] || {}
    }

    const updateBodyData = (bodyType: BodyType, newData: unknown) => {
      const existingBody = parseJsonData(form.state.values.http_request.body)
      const updatedBody = {
        ...existingBody,
        [bodyType]: newData,
      }
      form.setFieldValue('http_request.body', JSON.stringify(updatedBody, null, 2))
    }

    const getJsonBody = () => {
      const selectedOption = form.state.values.http_request.body_type
      const jsonBody = getBodyDataForType(selectedOption)
      const jsonBodyStringified = JSON.stringify(jsonBody, null, 2)
      const value = typeof jsonBody === 'string' ? jsonBody : jsonBodyStringified
      return value
    }

    return [
      {
        name: 'http_request.body_type',
        type: 'sub-item',
        componentType: 'radioGroup',
        zodValidation: z.enum(['none', 'form_data', 'json', 'form_urlencoded']),
        inputProps: {
          options: [
            { label: 'None', value: 'none' },
            { label: 'Form Data', value: 'form_data' },
            { label: 'JSON', value: 'json' },
            { label: 'Form URL Encoded', value: 'form_urlencoded' },
          ],
          optionsClassName: 'gap-3',
        },
      },
      {
        name: 'http_request.body',
        type: 'custom',
        onChangeListenTo: ['http_request.body_type'],
        visible: (values) => {
          const bodyType = values.http_request.body_type
          return bodyType !== 'none' && bodyType !== 'json'
        },
        component: () => {
          const selectedOption = form.state.values.http_request.body_type
          return (
            <div className="mt-4">
              <JsonKeyValueEditor
                showSecretVariables
                initialData={getBodyDataForType(selectedOption) as Record<string, string>}
                nodeVars={nodeVars}
                isJsonModeEnabled={selectedOption === 'json'}
                showJsonModeToggle={false}
                onChange={(newBody) => updateBodyData(selectedOption, newBody)}
              />
            </div>
          )
        },
        zodValidation: z.string().optional(),
      },
      {
        name: 'http_request.body',
        type: 'custom',
        onChangeListenTo: ['http_request.body_type'],
        visible: (values) => values.http_request.body_type === 'json',
        component: () => (
          <div className="mt-4">
            <CodeEditor
              className="rounded-md overflow-auto"
              value={getJsonBody()}
              onChange={(value) => updateBodyData(form.state.values.http_request.body_type, value)}
              suggestions={allSuggestions}
            />
          </div>
        ),
        zodValidation: z.string().optional(),
      },
    ]
  }, [form, nodeVars, allSuggestions])

  return <FieldsRenderer form={form} fields={fields} />
}
